'use client'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { atoms } from 'website/src/lib/atoms'
import { useRootProps } from 'website/src/lib/hooks'

import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    RadioGroup,
    useDisclosure,
} from '@nextui-org/react'
import { loadStripe } from '@stripe/stripe-js'
import { useThrowingFn } from 'beskar/landing'

import { ReactNode, useState } from 'react'
import { RadioCard } from 'website/src/components/form'

import {
    bannerHeight,
    githubProjectTypes,
    topWebsitePadding,
} from 'website/src/lib/constants'
import { AddonType, MIN_SEATS, env, prices } from 'website/src/lib/env'
import { createStripeCheckoutSession } from 'website/src/pages/api/stripe/stripe-functions'

function useUpgradeModal({ orgId, seatsUsed = 0 }) {
    const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure()
    const [yearlyBilling, setYearlyBilling] = useState(false)
    const [seats, setSeats] = useState(MIN_SEATS)
    const [addons, setAddons] = useState<AddonType[]>([])
    const seatPrice = yearlyBilling
        ? prices.yearlySeatBase
        : prices.monthlySeatBase
    const minSeats = Math.max(seats, MIN_SEATS, seatsUsed)
    let price = seats * seatPrice.usd
    for (let addon of addons) {
        if (addon === 'ai') {
            price += yearlyBilling
                ? prices.yearlyAiIntegration.usd
                : prices.monthlyAiIntegration.usd
        }
        if (addon === 'sso') {
            price += yearlyBilling
                ? prices.yearlySSOIntegration.usd
                : prices.monthlySSOIntegration.usd
        }
    }
    const router = useRouter()

    const { fn: checkout, isLoading: isCheckingOut } = useThrowingFn({
        async fn() {
            const stripe = await loadStripe(
                env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',
            )
            const { sessionId } = await createStripeCheckoutSession({
                orgId: orgId,
                yearlyBilling,
                seats: Number(seats) || minSeats,
                addons,
                callbackUrl: new URL(
                    `/org/${orgId}`,
                    env.NEXT_PUBLIC_URL,
                ).toString(),
            })

            await stripe?.redirectToCheckout({ sessionId })
        },
    })
    return {
        isLoading: isCheckingOut,
        onOpen,
        onClose,
        modal: (
            <Modal
                backdrop='opaque'
                size='2xl'
                isOpen={isOpen}
                onOpenChange={onOpenChange}
            >
                <ModalContent>
                    <ModalHeader className='flex flex-col gap-1'>
                        Upgrade
                    </ModalHeader>

                    <ModalBody
                        as='form'
                        onSubmit={async (e) => {
                            e.preventDefault()
                            await checkout()
                        }}
                        className='flex flex-col gap-8'
                    >
                        <RadioGroup
                            onValueChange={(x) =>
                                setYearlyBilling(x === 'true')
                            }
                            value={yearlyBilling ? 'true' : 'false'}
                            isRequired
                            className='space-y-3 w-full flex flex-col'
                            label={
                                <div className='dark:text-gray-100'>
                                    How often do you want to pay?
                                </div>
                            }
                        >
                            <RadioCard value={'true'}>Yearly</RadioCard>
                            <RadioCard value={'false'}>Monthly</RadioCard>
                        </RadioGroup>
                        <div className='space-y-2'>
                            <Input
                                type='number'
                                label='Number of editors (seats)'
                                labelPlacement='outside'
                                onValueChange={(x) => setSeats(x as any)}
                                value={seats as any}
                                min={minSeats}
                                max={100}
                            />
                            {seats < minSeats && (
                                <div className='text-sm text-red-400'>
                                    You can provision {minSeats} and above
                                </div>
                            )}
                        </div>
                        {/* <CheckboxGroup
                    onValueChange={setAddons as any}
                    className='space-y-3 w-full flex flex-col'
                    label={
                        <div className='dark:text-gray-100'>
                            What addons do you want to add?
                        </div>
                    }
                >
                    <CheckboxCard
                        description='$49 per month'
                        value={'ai' satisfies AddonType}
                    >
                        AI integration
                    </CheckboxCard>
                    <CheckboxCard
                        description='$49 per month'
                        value={'sso' satisfies AddonType}
                    >
                        SSO
                    </CheckboxCard>
                </CheckboxGroup> */}
                        <div className='flex flex-col gap-2'>
                            <Button
                                isDisabled={seats < MIN_SEATS}
                                isLoading={isCheckingOut}
                                type='submit'
                            >
                                Upgrade for{' '}
                                <span className='font-mono font-semibold tracking-wide'>
                                    ${price}
                                </span>{' '}
                                a month
                            </Button>
                            <div className='text-xs opacity-60 text-center'>
                                Powered by{' '}
                                <span className='font-semibold'>Stripe</span>
                            </div>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        ),
    }
}

export function Banner() {
    let { freeTrialEndsInDays, subs, userId, orgId, projectType } =
        useRootProps()
    const { onOpen: openUpgrade, modal } = useUpgradeModal({ orgId })

    let content: ReactNode = ''
    if (subs?.length) {
        content = null
    } else if (projectType === 'demo-project') {
        content = (
            <div className=''>
                <GhostButton
                    onClick={() => {
                        atoms.showSignupModal.set(true)
                    }}
                >
                    sign up
                </GhostButton>{' '}
                to save your work!
            </div>
        )
    } else if (!userId && githubProjectTypes.includes(projectType)) {
        content = (
            <div className=''>
                <GhostButton
                    onClick={() => {
                        atoms.showSignupModal.set(true)
                    }}
                >
                    sign up
                </GhostButton>{' '}
                to suggest changes
            </div>
        )
    } else if (freeTrialEndsInDays) {
        content = (
            <div className=''>
                <GhostButton onClick={openUpgrade}>upgrade</GhostButton> to not
                lose access to Pro features in{' '}
                {freeTrialEndsInDays === 1 ? 'day' : 'days'}
            </div>
        )
    } else if (!freeTrialEndsInDays && projectType === 'org-project') {
        content = (
            <div className=''>
                <GhostButton onClick={openUpgrade}>upgrade</GhostButton> to get
                AI integration, members and more
            </div>
        )
    }

    if (!content) {
        atoms.topWebsitePadding.set(topWebsitePadding)
        return
    } else {
        atoms.topWebsitePadding.set(topWebsitePadding + bannerHeight)
    }

    return (
        <div
            style={{ height: bannerHeight }}
            className='font-semibold text-white bg-gradient-to-r from-cyan-600 gap-1 to-sky-600 text-sm absolute top-0 shrink-0 bg-black w-full flex items-center justify-center'
        >
            {content}
            {modal}
        </div>
    )
}

function GhostButton({ ...rest }) {
    return (
        <button
            className={classNames(
                'inline appearance-none px-[6px] py-[1px] transition-all mx-1 bg-gray-100/10 hover:bg-gray-100/30 rounded',
            )}
            {...rest}
        />
    )
}
